import { Link } from "@tanstack/react-router";
import { type ComponentProps, createContext, useContext } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const LinkCardContext = createContext(false);

/** Class for nested interactive children of a `LinkCard`: they sit above the overlay. */
export const LINK_CARD_SLOT = "relative z-10";

/**
 * A block `Card` that one `StretchedLink` stretches over. `relative isolate` makes it the
 * containing block of the overlay. Only for block containers: never inside table rows (WebKit
 * ignores positioning on `<tr>`), rows use `RowLinkRow` from `row-link.tsx`.
 */
export function LinkCard({ className, ...props }: ComponentProps<typeof Card>) {
  return (
    <LinkCardContext.Provider value>
      <Card className={cn("relative isolate", className)} {...props} />
    </LinkCardContext.Provider>
  );
}

/**
 * The link whose `::after` covers the surrounding `LinkCard`. This file is the only place that
 * may use `after:absolute` / `after:inset-0` (guarded by `link-targets.test.ts`). Throws outside
 * a `LinkCard` in development and tests, where the overlay would span an unrelated ancestor.
 */
export function StretchedLink({ className, ...props }: ComponentProps<typeof Link>) {
  const inside = useContext(LinkCardContext);
  if (!inside && import.meta.env.MODE !== "production") {
    throw new Error("StretchedLink must be rendered inside a LinkCard");
  }
  return (
    <Link
      className={cn("outline-none after:absolute after:inset-0 after:rounded-[inherit]", className)}
      {...props}
    />
  );
}
